import api from "@/lib/apiClient";

export const teacherApi = {
  list: () => api.get("/api/teachers"),
  create: (payload) => api.post("/api/teachers", payload),
  inviteBatch: (payload) => api.post("/api/teachers/invite-batch", payload), // PR-3a — batch cohort invite
  createSelfProfile: (payload) => api.post("/api/teachers/self-profile", payload),
  currentProfile: () => api.get("/api/teachers/me/profile"),
  updateCurrentProfile: (payload) => api.patch("/api/teachers/me/profile", payload),
  myReferenceImages: () => api.get("/api/teachers/me/reference-images"),
  uploadReferenceImage: (formData) =>
    api.post("/api/teachers/me/reference-images", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  // REG-1b: dry-run validation of a candidate reference (guided capture retake-on-fail).
  validateReferenceImage: (formData) =>
    api.post("/api/teachers/me/reference-images/validate", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  deleteReferenceImage: (imageId) => api.delete(`/api/teachers/me/reference-images/${imageId}`),
  myDashboard: (params) => api.get("/api/teachers/me/dashboard", { params }),
  mySearch: (params) => api.get("/api/teachers/me/search", { params }),
  myLessons: (params) => api.get("/api/teachers/me/lessons", { params }),
  myCoaching: () => api.get("/api/teachers/me/coaching"),
  createReflection: (payload) => api.post("/api/teachers/me/reflections", payload),
  updateCoachingTask: (taskId, payload) => api.patch(`/api/coaching/tasks/${taskId}`, payload),
  myRecognition: () => api.get("/api/teachers/me/recognition"),
  // FIN-2 item 3: the teacher schedules a Cognivio (a live observation session) for herself.
  requestCognivioSession: (payload) => api.post("/api/teachers/me/cognivio-sessions", payload),
  // ADMIN-A6a: her own sessions (to answer a requested one) + accept/decline.
  cognivioSessions: () => api.get("/api/teachers/me/cognivio-sessions"),
  transitionCognivioSession: (sessionId, status) =>
    api.patch(`/api/teachers/me/cognivio-sessions/${sessionId}`, { status }),
  // ADMIN-A8: the teacher's own timetable (weekday × period → subject/class). Teacher-owned.
  myTimetable: () => api.get("/api/teachers/me/timetable"),
  saveTimetable: (rows) => api.put("/api/teachers/me/timetable", { rows }),
  // W58: read an uploaded class schedule (ICS, CSV, XLSX, photo/PDF) into a PREVIEW. Writes nothing;
  // the confirm is saveTimetable. The *For variants are an admin acting for one teacher.
  importTimetable: (file) => {
    const body = new FormData();
    body.append("file", file);
    return api.post("/api/teachers/me/timetable/import", body);
  },
  timetableFor: (teacherId) => api.get(`/api/teachers/${teacherId}/timetable`),
  saveTimetableFor: (teacherId, rows) => api.put(`/api/teachers/${teacherId}/timetable`, { rows }),
  importTimetableFor: (teacherId, file) => {
    const body = new FormData();
    body.append("file", file);
    return api.post(`/api/teachers/${teacherId}/timetable/import`, body);
  },
  // GOALS-1: the teacher's own growth goals (+ the area vocabulary for the picker) + CRUD.
  myGoals: () => api.get("/api/teachers/me/goals"),
  createGoal: (payload) => api.post("/api/teachers/me/goals", payload),
  updateGoalStatus: (goalId, status) => api.patch(`/api/teachers/me/goals/${goalId}`, { status }),
  // F-27: her own goal's wording, and whether it is one of "this week's goals" on Home.
  updateGoal: (goalId, fields) => api.patch(`/api/teachers/me/goals/${goalId}`, fields),
  // W29 v1 (C-319): baseline -> now -> word for every goal she may read, with her own evidence counts.
  myGoalProgress: () => api.get("/api/teachers/me/goal-progress"),
  get: (id) => api.get(`/api/teachers/${id}`),
  // T4: the teacher's own numeric trend series (owner short-circuit → sees all own lessons,
  // deduped by T2). Feeds the Home growth sparkline.
  dashboard: (id, params) => api.get(`/api/teachers/${id}/dashboard`, { params }),
  // T7-UI: the My Growth page — focus-area trends + per-lesson time-pointer moments.
  myGrowth: () => api.get("/api/teachers/me/growth"),
  myGrowthMoments: (areaId, params) => api.get(`/api/teachers/me/growth/${areaId}/moments`, { params }),
  // GOALS-2B: Mira's stored growth notes (Layer 2) — latest per subject, owner-scoped.
  myGrowthNotes: () => api.get("/api/teachers/me/growth/notes"),
  // REMIND-1: Mira's pending in-app rhythm touches (owner-scoped) + dismiss/act.
  myTouches: () => api.get("/api/teachers/me/touches"),
  updateTouchStatus: (touchId, status) => api.patch(`/api/teachers/me/touches/${touchId}`, { status }),
  update: (id, payload) => api.patch(`/api/teachers/${id}`, payload),
  conferencePrep: (id) => api.get(`/api/teachers/${id}/conference-prep`),
  conferenceAgenda: (id) => api.get(`/api/teachers/${id}/conference-agenda`),
  evidenceCatalog: (id) => api.get(`/api/teachers/${id}/evidence-catalog`),
  adaptiveSupport: (id) => api.get(`/api/teachers/${id}/adaptive-support`),
  publishConferenceAgenda: (id, payload) =>
    api.post(`/api/teachers/${id}/conference-agenda`, payload),
  coachingTimeline: (id) => api.get(`/api/teachers/${id}/coaching-timeline`),
  coachingTasks: (params) => api.get("/api/coaching/tasks", { params }),
  getPeerRecommendations: (id) =>
    api.get(`/api/teachers/${id}/peer-recommendations`),
  // PR C6/C7: artifact-driven coaching workflow.
  myCoachingThread: (params) =>
    api.get("/api/teachers/me/coaching-thread", { params }),
  actionItemTried: (actionItemId, payload) =>
    api.post(`/api/teachers/me/action-items/${actionItemId}/tried`, payload),
  actionItemReflect: (actionItemId, payload) =>
    api.post(`/api/teachers/me/action-items/${actionItemId}/reflect`, payload),
};

// T9 — peer recognition (the Recognition wall + composer). Distinct from the badges/accolades
// `recognitionApi` (system-awarded gold stars). Backed by the `recognized_moments` collection.
export const recognitionWallApi = {
  create: (payload) => api.post("/api/recognitions", payload),
  wall: (params) => api.get("/api/recognitions/wall", { params }),
  mine: () => api.get("/api/recognitions/mine"),
  // COLLAB-1 — consult threads, direct invites, gratitude, the private helper counter.
  colleagues: () => api.get("/api/recognitions/colleagues"),
  replies: (id) => api.get(`/api/recognitions/${id}/replies`),
  reply: (id, body) => api.post(`/api/recognitions/${id}/replies`, { body }),
  deleteReply: (id, replyId) => api.delete(`/api/recognitions/${id}/replies/${replyId}`),
  closeThread: (id, consult_closed) => api.patch(`/api/recognitions/${id}/close`, { consult_closed }),
  thankReply: (id, replyId) => api.post(`/api/recognitions/${id}/replies/${replyId}/thanks`),
  helpCount: () => api.get("/api/recognition/my-help-count"),
  // COLLAB-2a verb 2-lite — "ניסיתי": mark/un-mark a shared moment tried; whoTried is originator-only.
  tried: (id) => api.post(`/api/recognitions/${id}/tried`),
  untried: (id) => api.delete(`/api/recognitions/${id}/tried`),
  whoTried: (id) => api.get(`/api/recognitions/${id}/tried`),
};

// TROOM-2c — the coach-note DYAD conversation (teacher-owner ⇄ note-coach). Keyed on the note id;
// list/create/delete only (close OFF, gratitude OFF — those routes don't exist). Storage is
// moment_replies, never video_comments threading (the admin dashboard reads that surface).
export const coachNoteApi = {
  replies: (noteId) => api.get(`/api/coach-notes/${noteId}/replies`),
  reply: (noteId, body) => api.post(`/api/coach-notes/${noteId}/replies`, { body }),
  deleteReply: (noteId, replyId) => api.delete(`/api/coach-notes/${noteId}/replies/${replyId}`),
};

// FB-1b — the per-shared-lesson COACH BOX (admin ⇄ teacher, one thread per lesson). Keyed on the
// lesson's video_id; list/create/delete only. Same moment_replies storage as the dyad above, so it
// inherits the getsource fence; the admin voice is SAFE-gated server-side (422 LESSON_THREAD_UNSAFE),
// the teacher-owner is ungated. Shape matches <MomentThread>'s api contract (replies/reply/deleteReply).
export const lessonThreadApi = {
  replies: (videoId) => api.get(`/api/lessons/${videoId}/thread`),
  reply: (videoId, body) => api.post(`/api/lessons/${videoId}/thread`, { body }),
  deleteReply: (videoId, replyId) => api.delete(`/api/lessons/${videoId}/thread/${replyId}`),
};

// PR C7: admin-side API methods for the teacher coaching workflow.
export const adminCoachingApi = {
  getReview: (assessmentId) =>
    api.get(`/api/admin/assessments/${assessmentId}/teacher-feedback-review`),
  upsertReview: (assessmentId, payload) =>
    api.post(`/api/admin/assessments/${assessmentId}/teacher-feedback-review`, payload),
  getThread: (teacherId, params) =>
    api.get(`/api/admin/teachers/${teacherId}/coaching-thread`, { params }),
  postThreadMessage: (teacherId, payload) =>
    api.post(`/api/admin/teachers/${teacherId}/coaching-thread`, payload),
  auditArtifacts: (params) =>
    api.get("/api/admin/teacher-coaching-artifacts/audit", { params }),
};

export const privacyProfileApi = {
  get: (teacherId) => api.get(`/api/teachers/${teacherId}/privacy-profile`),
  upload: (teacherId, formData) =>
    api.post(`/api/teachers/${teacherId}/privacy-profile`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  remove: (teacherId) =>
    api.delete(`/api/teachers/${teacherId}/privacy-profile`),
};

/**
 * C-167 §1 — the observer-goals routes, which had NO frontend caller at all until now (ADMIN-WS
 * measured that: the backend path existed and no screen called it).
 *
 * THREE THINGS THE BACKEND'S OWN COMMENTS DECIDE, encoded here so a caller cannot get them wrong:
 *
 * 1. `boundary_kind` / `boundary_id` ARE NOT SENT. The model accepts them "SO [they] CAN BE CHECKED,
 *    never so [they] can decide" (server.py:9022-9025): the boundary is derived server-side from the
 *    caller's membership, an echoed value is tolerated and a DIFFERENT one is refused. So `create`
 *    takes no boundary argument and offers no way to pass one.
 * 2. `goal_type` IS NOT SENT for school scope. Its enum "describes THE OBSERVER'S OWN PRACTICE and
 *    fits nothing about a teacher" (:8993-8996), and C-167 §1 rules it absent for school scope.
 * 3. ARCHIVE IS `active: false`, NEVER `achieved`. The model carries both pairs separately
 *    (`active`/`deactivated_at` and `achieved`/`achieved_at`), and they mean different things: a
 *    goal no longer pursued versus one accomplished. `archive` is named for what it does so no
 *    caller reaches for `achieved` to mean "we stopped".
 */
export const observerGoalApi = {
  // Active AND inactive, per the route's own docstring — the caller filters for display.
  listSchool: () => api.get("/api/observer/goals", { params: { scope: "school" } }),
  // W27 P0 (C-270 §1): there is NO teacher-facing wrapper here on purpose. The dedicated route
  // `/api/teachers/me/observer-goals` returns the ADMIN serializer, which carries observer free
  // text (`progress_signals[].note`) and tenancy plumbing she is not the reader for -- see the
  // A17 allow-list note in server.py. Her pages read `assigned_goals` off
  // `GET /api/teachers/me/goals` instead: the allow-listed projection, on a call they already
  // make, and degradation-safe for a teacher with no school. #815 called the wrong one; this is
  // the correction, and the wrapper is gone so it cannot be reached for again.
  create: ({ goalText, targetMetric }) =>
    api.post("/api/observer/goals", {
      goal_text: goalText,
      target_metric: targetMetric,
      scope: "school",
    }),
  // W27 (C-238 §2, owner): "no way to set goals for each teacher". The BACKEND already accepts a
  // teacher-scoped goal -- POST /api/observer/goals takes scope + teacher_id, and
  // observer_goals.create_goal REFUSES a teacher outside the administrator's own boundary. What
  // was missing is this: `create` above hardcodes scope "school" and sends no teacher, so no
  // caller could ever reach that path.
  //
  // The boundary is NOT sent and must not be: the server derives it from the administrator's own
  // membership and ignores any supplied in the payload. Echoing one here would look like it
  // mattered and quietly diverge the day the server's derivation changed.
  // W27 UI — the READ that matched `createForTeacher`'s gap. `GET /api/observer/goals` has always
  // taken `scope` and `teacher_id` (`list_goals_for_admin` applies both), and the query is already
  // scoped to the administrator's own boundary — so a teacher outside it returns nothing rather
  // than erroring. Only this caller was missing, exactly as `createForTeacher` was.
  //
  // ACTIVE AND INACTIVE, per the route's own docstring: the panel filters for display and marks
  // the archived ones, because "an administrator managing goals needs to see what she deactivated".
  listForTeacher: (teacherId) =>
    api.get("/api/observer/goals", { params: { scope: "teacher", teacher_id: teacherId } }),
  createForTeacher: ({ teacherId, goalText, targetMetric }) =>
    api.post("/api/observer/goals", {
      goal_text: goalText,
      target_metric: targetMetric,
      scope: "teacher",
      teacher_id: teacherId,
    }),
  // W42 UI (C-270 / C-127) — the THIRD scope: one subject-faculty department. The department is
  // a plain string from the organisation's own vocabulary (`/api/admin/departments`); the server
  // canonicalises it to the organisation's spelling and refuses one the organisation lacks. It is
  // IMMUTABLE after create (observer_goals.IMMUTABLE_FIELDS), so `update` below never sends it.
  listDepartment: () => api.get("/api/observer/goals", { params: { scope: "department" } }),
  createForDepartment: ({ department, goalText, targetMetric }) =>
    api.post("/api/observer/goals", {
      goal_text: goalText,
      target_metric: targetMetric,
      scope: "department",
      department,
    }),
  update: (goalId, { goalText, targetMetric }) =>
    api.patch(`/api/observer/goals/${goalId}`, {
      goal_text: goalText,
      target_metric: targetMetric,
    }),
  archive: (goalId) => api.patch(`/api/observer/goals/${goalId}`, { active: false }),
  restore: (goalId) => api.patch(`/api/observer/goals/${goalId}`, { active: true }),
  // W29 v1 (C-319): a WORD per goal in her boundary, never a count. With a teacher, that teacher's
  // admin-set goals only; without, every goal (school and department as a k=3 aggregate).
  progress: (teacherId) =>
    api.get("/api/observer/goal-progress", { params: teacherId ? { teacher_id: teacherId } : {} }),
};

export const observationApi = {
  create: (payload) => api.post("/api/observations", payload),
  listForTeacher: (teacherId) =>
    api.get(`/api/teachers/${teacherId}/observations`),
  listForVideo: (videoId) =>
    api.get(`/api/videos/${videoId}/observations`),
  update: (id, payload) => api.patch(`/api/observations/${id}`, payload),
};

export const actionPlanApi = {
  get: (teacherId) => api.get(`/api/teachers/${teacherId}/action-plan`),
  history: (teacherId) => api.get(`/api/teachers/${teacherId}/action-plan/history`),
  save: (teacherId, payload) =>
    api.post(`/api/teachers/${teacherId}/action-plan`, payload),
};
